import { Button, Card, Stack } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';

import { GeneralEmptyState } from '@/components/generalEmptyState';
import { Loader } from '@/components/layout/loading';
import { Panels } from '@/components/panels/panels';
import {
  resourceType,
  cloudProvider,
  dataClasses,
  environment,
  apps,
  sourceAccountId,
  sourceRegion,
  vpc,
  subnets,
  resourceGroupName,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
} from '@/components/queryBuilder/properties';
import {
  getResourcesScopeType,
  type ResourcesScopeType,
} from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import {
  hasValidConditionValues,
  isAnAllCondition,
  type FilterProperty,
} from '@/types/advanceFilter';

import { RansomwareByConditionViewerHeader } from './ransomwareByConditionViewerHeader';
import {
  ransomwareConditionEditorAllExcludedDescription,
  ransomwareConditionEditorAllIncludedDescription,
} from './ransomwareConditionEditor';

export const ransomwareSupportedProperties: FilterProperty[][] = [
  [resourceType, dataClasses, environment, apps],
  [
    cloudProvider,
    sourceAccountId,
    sourceRegion,
    vpc,
    subnets,
    resourceGroupName,
  ],
  [resourceName, resourceId],
  [tagKeys, tagKeyValues],
];

export const AssignRansomwareByCondition = () => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const { rightPanel, snackbar } = useWorkspace();

  const {
    data: securityPlan,
    isLoading,
    mutate,
  } = dal.security.useSecurityPlan();

  const isPlanAssigned =
    hasValidConditionValues(securityPlan?.condition) ||
    (securityPlan?.condition && isAnAllCondition(securityPlan.condition));
  const resourcesScopeType: ResourcesScopeType = getResourcesScopeType(
    securityPlan?.condition,
    'allExcluded'
  );

  const openConditionEditor = () => {
    rightPanel.setComponent({
      panel: Panels.RansomwareConditionPanel,
      props: {
        condition:
          resourcesScopeType === 'useConditions'
            ? securityPlan?.condition
            : undefined,
        resourcesScopeType,
        include: securityPlan?.include,
        exclude: securityPlan?.exclude,
        onSave: async (condition, include, exclude) => {
          await save(condition, include, exclude);
          snackbar.showMessage('Ransomware condition was set');
        },
        onAbort: closePanel,
      },
    });
    rightPanel.setIsOpen(true, PanelSize.Full);
  };

  const onEditClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    openConditionEditor();
  };

  const closePanel = () => {
    rightPanel.setIsOpen(false);
  };

  const save = async (
    condition?: Condition,
    include?: string[],
    exclude?: string[]
  ) => {
    await dal.security.saveSecurityPlan(condition, include, exclude);
    await mutate();
    closePanel();
  };

  if (isLoading) {
    return <Loader />;
  }

  return !isPlanAssigned ? (
    <GeneralEmptyState
      title='Protect resources with condition-based monitoring'
      description='Create dedicated detection rules based on resource properties, like data classes, environment, or tags. Matching backed-up resources will be checked for ransomware.'
      button={
        <Button
          onClick={onEditClick}
          variant='outlined'
          disabled={!isAuthorized('update:security_plan')}
        >
          Create Conditions
        </Button>
      }
    />
  ) : (
    <Card
      sx={{
        padding: '24px 40px',
        border: '1px solid var(--mui-palette-divider)',
      }}
    >
      <Stack alignItems='start' gap='16px'>
        <ResourcesSelectionViewer
          condition={securityPlan?.condition}
          conditionTitle='Check resources meeting these conditions for ransomware:'
          include={securityPlan?.include ?? []}
          exclude={securityPlan?.exclude ?? []}
          resourcesScopeType={resourcesScopeType}
          dynamicResourcesHeader={
            <RansomwareByConditionViewerHeader
              onSave={save}
              onEdit={onEditClick}
            />
          }
          allExcludedDescription={
            ransomwareConditionEditorAllExcludedDescription
          }
          allIncludedDescription={
            ransomwareConditionEditorAllIncludedDescription
          }
          supportProperties={ransomwareSupportedProperties.flat()}
        />
      </Stack>
    </Card>
  );
};
